/**
 * Fetcher class for making HTTP requests.
 */
class Fetcher {
  /**
   * Create a Fetcher instance.
   * @param {string} baseurl - Base URL for the HTTP requests.
   * @param {Object} options - Additional options for the Fetcher instance.
   */
  constructor(baseurl, options) {
    this.baseurl = baseurl
    this.options = options
    this.interceptor = { request: (e) => e, response: (e) => e }
  }

  /**
   * Configure the base URL and options for the Fetcher instance.
   * @param {string} baseurl - Base URL for the HTTP requests.
   * @param {Object} options - Additional options for the Fetcher instance.
   */
  config(baseurl, options) {
    this.baseurl = baseurl
    this.options = options
  }

  /**
   * Set a request middleware for the Fetcher instance.
   * @param {Function} callback - Callback function for request middleware.
   */
  requestMiddleware(callback) {
    this.interceptor.request = callback
  }

  /**
   * Set a response middleware for the Fetcher instance.
   * @param {Function} callback - Callback function for response middleware.
   */
  responseMiddleware(callback) {
    this.interceptor.response = callback
  }

  /**
   * Perform a common HTTP request with the specified method, URL, data, and options.
   * @param {Object} requestConfig - Configuration object for the HTTP request.
   * @param {string} requestConfig.method - HTTP method (e.g., 'GET', 'POST').
   * @param {string} requestConfig.url - URL for the HTTP request.
   * @param {Object} requestConfig.data - Data payload for the request.
   * @param {Object} requestConfig.options - Additional options for the request.
   * @returns {Promise} A promise that resolves with the response data.
   */
  async commonRequest({ method, url, data, options }) {
    const isFormData = data instanceof FormData

    const payload = {
      method,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...options?.headers
      },
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      ...options
    }
    if (data) {
      payload.body = isFormData ? data : JSON.stringify(data)
    }
    const response = await fetch(url, this.interceptor.request(payload, url))
    if (options?.responseType === 'blob') {
      const blob = await response.blob()
      return this.interceptor.response(blob, url)
    } else {
      const result = await response.json()
      if (!response.ok) {
        const error = new Error(result?.sMessage || result?.error?.sMessage || response.statusText)
        // console.log(`\x1b[91mForm Fetcher: ${error.message}\x1b[0m`)
        console.error(`Fetcher Error: ${error.message}`, {
          status: response.status,
          url,
          method,
          responseData: result
        })
        error.status = response.status
        error.data = result
        this.interceptor.response(error, url)
        throw error
        // return error
      }
      return this.interceptor.response(result, url)
    }
  }

  /**
   * Perform a GET request.
   * @param {string} url - URL for the GET request.
   * @param {Object} options - Additional options for the request.
   * @returns {Promise} A promise that resolves with the response data.
   */
  async get(url, options) {
    return await this.commonRequest({ method: 'GET', url: this.baseurl + url, options })
  }

  /**
   * Perform a POST request.
   * @param {string} url - URL for the POST request.
   * @param {Object} data - Data payload for the request.
   * @param {Object} options - Additional options for the request.
   * @returns {Promise} A promise that resolves with the response data.
   */
  async post(url, data, options) {
    return await this.commonRequest({ method: 'POST', url: this.baseurl + url, data, options })
  }

  /**
   * Perform a PUT request.
   * @param {string} url - URL for the PUT request.
   * @param {Object} data - Data payload for the request.
   * @param {Object} options - Additional options for the request.
   * @returns {Promise} A promise that resolves with the response data.
   */
  async put(url, data, options) {
    return await this.commonRequest({ method: 'PUT', url: this.baseurl + url, data, options })
  }

  /**
   * Perform a DELETE request.
   * @param {string} url - URL for the DELETE request.
   * @param {Object} data - Data payload for the request.
   * @param {Object} options - Additional options for the request.
   * @returns {Promise} A promise that resolves with the response data.
   */
  async delete(url, data, options) {
    return await this.commonRequest({ method: 'Delete', url: this.baseurl + url, data, options })
  }
}

export default Fetcher
